import { eventBus, Events } from '../core/EventBus.js';
import { gameState } from '../core/GameState.js';
import { AUDIO } from '../core/Constants.js';

/**
 * Chiptune SFX synthesised on the fly — square/saw blips, no audio assets.
 * The context stays suspended until the first user gesture.
 */
export class AudioSystem {
  constructor() {
    this.ctx = null;
    this.master = null;

    this.unsub = [
      eventBus.on(Events.PLAYER_HOP, () => this.blip(AUDIO.HOP)),
      eventBus.on(Events.PLAYER_BLOCKED, () => this.blip(AUDIO.BUMP)),
      eventBus.on(Events.PLAYER_BOARD, () => this.blip(AUDIO.BOARD)),
      eventBus.on(Events.PLAYER_DIE, (d) => this.death(d)),
      eventBus.on(Events.LEVEL_COMPLETE, () => this.arp(AUDIO.GOAL, 0.09)),
      eventBus.on(Events.GAME_OVER, () => this.arp(AUDIO.GAME_OVER, 0.16, 'square')),
      eventBus.on(Events.INPUT_MUTE, () => this.toggleMute()),
      eventBus.on('cobra:hiss', () => this.hiss()),
      eventBus.on('flood:warn', () => this.siren()),
      eventBus.on('flood:surge', () => this.waterSurge()),
      eventBus.on('train:crash', () => this.crash()),
      eventBus.on('zap:sound', () => this.zapSound()),
      eventBus.on('curry:immunity', () => this.arp([440, 554, 659, 880], 0.08, 'sawtooth')),
      eventBus.on('curry:poison', () => this.tone({ freq: 220, to: 110, dur: 0.35, type: 'sawtooth', gain: 0.9 })),
    ];
  }

  ensure() {
    if (!this.ctx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = gameState.game.muted ? 0 : AUDIO.MASTER;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
    return this.ctx;
  }

  toggleMute() {
    gameState.game.muted = !gameState.game.muted;
    if (this.master) this.master.gain.value = gameState.game.muted ? 0 : AUDIO.MASTER;
    eventBus.emit(Events.AUDIO_TOGGLE, { muted: gameState.game.muted });
  }

  tone({ freq, to, dur, type = 'square', delay = 0, gain = 1 }) {
    const ctx = this.ensure();
    if (!ctx || gameState.game.muted) return;
    const t0 = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (to && to !== freq) osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), t0 + dur);
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.exponentialRampToValueAtTime(gain, t0 + 0.008);
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(env).connect(this.master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  blip(spec) { this.tone({ ...spec }); }

  death(data) {
    if (data && data.cause === 'cobra') {
      this.tone({ freq: 880, to: 160, dur: 0.18, type: 'sawtooth', gain: 1.1 });
      this.tone({ freq: 440, to: 80, dur: 0.28, type: 'square', gain: 0.9 });
      this.hiss(0.45);
    } else if (data && data.cause === 'flood') {
      this.waterSurge();
      this.tone({ freq: 320, to: 50, dur: 0.4, type: 'sawtooth', gain: 1.1 });
    } else {
      this.tone({ freq: 240, to: 35, dur: 0.28, type: 'sawtooth', gain: 1.2 });
      this.tone({ freq: 480, to: 90, dur: 0.14, type: 'square', gain: 0.9 });
      this.noise(0.35);
    }
  }

  siren() {
    this.tone({ freq: 320, to: 680, dur: 0.35, type: 'sawtooth', gain: 0.8 });
    this.tone({ freq: 680, to: 320, dur: 0.35, type: 'sawtooth', delay: 0.38, gain: 0.8 });
  }

  waterSurge() {
    this.noise(1.1);
    this.tone({ freq: 110, to: 35, dur: 0.85, type: 'sawtooth', gain: 0.85 });
  }

  crash() {
    this.tone({ freq: 190, to: 40, dur: 0.3, type: 'sawtooth', gain: 1.2 });
    this.noise(0.4);
  }

  zapSound() {
    this.tone({ freq: 1500, to: 350, dur: 0.05, type: 'sawtooth', gain: 0.35 });
    this.noise(0.04);
  }

  hiss(dur = 0.4) {
    const ctx = this.ensure();
    if (!ctx || gameState.game.muted) return;
    const frames = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) {
      const env = Math.sin(Math.PI * (i / frames));
      data[i] = (Math.random() * 2 - 1) * env * 0.3;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 3200;
    const g = ctx.createGain();
    g.gain.value = 0.45;
    src.connect(filter).connect(g).connect(this.master);
    src.start();
  }

  noise(dur) {
    const ctx = this.ensure();
    if (!ctx || gameState.game.muted) return;
    const frames = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / frames) ** 2;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = 0.5;
    src.connect(g).connect(this.master);
    src.start();
  }

  arp(freqs, step, type = 'triangle') {
    freqs.forEach((f, i) => {
      this.tone({ freq: f, to: f, dur: step * 1.6, type, delay: i * step, gain: 0.8 });
    });
  }

  dispose() {
    this.unsub.forEach((u) => u());
    if (this.ctx) this.ctx.close();
  }
}
